import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { SACRAMENTS, SACRAMENT_IMAGES } from '../data/mock';
import EditableText from '../components/EditableText';

const DETAILS = {
  'baptism': {
    image: SACRAMENT_IMAGES.baptism,
    quote: '“Holy Baptism is the basis of the whole Christian life, the gateway to life in the Spirit, and the door which gives access to the other sacraments.”',
    citation: 'Catechism of the Catholic Church, 1213',
    body: [
      'Many Catholics receive the sacrament of baptism as infants, in the presence of their parents and godparents. Through the saving waters of baptism, Catholics believe that we are washed clean from the stain of original sin caused by the sin of Adam and Eve.',
      'The grace we receive in baptism also makes us children of God the Father and welcomes us into the Catholic Church.',
      'For parents who would like their child to be baptized, baptismal registration, preparation, and scheduling at our parish can be arranged with our pastor or deacon through the parish office.',
      'For teenagers (ages 13+) and adults who wish to be baptized, please make an appointment with the parish office to speak with our pastor or contact our RCIA coordinator for guidance and preparation.'
    ]
  },
  'first-communion': {
    image: SACRAMENT_IMAGES.communion,
    quote: '“The Eucharist is the source and summit of the Christian life.”',
    citation: 'Lumen Gentium, 11',
    body: [
      'First Holy Communion is the moment when a baptized Catholic first receives the Body, Blood, Soul, and Divinity of Jesus Christ in the Holy Eucharist. It is one of the most joyful days in the life of a young Catholic and their family.',
      'Children in Grade 2 and older who have been baptized are invited to prepare for First Communion through our parish PREP catechism program. Preparation typically takes place over the course of a school year, culminating in the celebration of the sacrament in the spring.',
      'For adults who have not yet received First Communion, preparation is offered through our RCIA program.'
    ]
  },
  'confirmation': {
    image: SACRAMENT_IMAGES.confirmation,
    quote: '“By the sacrament of Confirmation, [the baptized] are more perfectly bound to the Church and are enriched with a special strength of the Holy Spirit.”',
    citation: 'Lumen Gentium, 11',
    body: [
      'In Confirmation, the baptized are sealed with the gift of the Holy Spirit and strengthened for the mission of Christian witness. It completes the grace of Baptism.',
      'Confirmation preparation at All Saints is offered to youth in Grade 7 and above through our parish PREP program.',
      'Adults seeking Confirmation are welcomed through our RCIA program. Please contact the parish office to begin preparation.'
    ]
  },
  'confession': {
    image: SACRAMENT_IMAGES.confession,
    quote: '“It is called the sacrament of Reconciliation, because it imparts to the sinner the love of God who reconciles.”',
    citation: 'Catechism of the Catholic Church, 1424',
    body: [
      'The Sacrament of Reconciliation (also known as Confession or Penance) is a powerful encounter with the mercy of God. Through the ministry of a priest, Christ forgives the sins we have committed after Baptism and restores us to friendship with God.',
      'Confessions are heard at St. Mary\'s on Saturdays from 4:15 to 4:45 PM, and at St. Joseph\'s on Saturdays from 9:30 to 10:00 AM. Confession is also available by appointment — please contact the parish office.'
    ]
  },
  'anointing-of-the-sick': {
    image: SACRAMENT_IMAGES.anointing,
    quote: '“Is anyone among you sick? Let them call the elders of the church to pray over them and anoint them with oil in the name of the Lord.”',
    citation: 'James 5:14',
    body: [
      'The Anointing of the Sick is a sacrament that offers spiritual healing, strength, and comfort to those who are seriously ill, undergoing major surgery, or approaching the end of life.',
      'If you or a loved one is in need of the Anointing of the Sick, please contact the parish office as soon as possible. For after-hours emergencies, please call the pastor\'s cellphone (available in the bulletin).',
      'This sacrament is not only for those at the point of death — anyone facing serious illness is encouraged to receive it.'
    ]
  },
  'marriage': {
    image: SACRAMENT_IMAGES.marriage,
    quote: '“What God has joined together, let no one separate.”',
    citation: 'Mark 10:9',
    body: [
      'The Sacrament of Marriage is a covenant by which a man and a woman establish between themselves a partnership of the whole of life, ordered by its nature to the good of the spouses and the procreation and education of offspring.',
      'Couples wishing to be married at All Saints Catholic Parish should contact the pastor at least six months in advance of their proposed wedding date. This allows time for the required marriage preparation program.',
      'Marriage preparation typically includes meetings with the pastor, a Catholic marriage preparation course, and the completion of necessary paperwork.'
    ]
  },
  'holy-orders': {
    image: SACRAMENT_IMAGES['holy-orders'],
    quote: '“The harvest is plentiful, but the labourers are few; therefore ask the Lord of the harvest to send out labourers into his harvest.”',
    citation: 'Matthew 9:37–38',
    body: [
      'Holy Orders is the sacrament through which the mission entrusted by Christ to his apostles continues to be exercised in the Church — through the ordained ministry of bishops, priests, and deacons.',
      'If you are discerning a call to the priesthood, diaconate, or religious life, please speak with our pastor. He would be honoured to walk with you on your journey of discernment.',
      'The Diocese of Victoria Vocations Office is also a wonderful resource for those exploring this calling.'
    ]
  }
};

const SacramentDetail = () => {
  const { slug } = useParams();
  const sacrament = SACRAMENTS.find(s => s.slug === slug);
  const detail = DETAILS[slug];

  if (!sacrament || !detail) return <Navigate to="/sacraments" replace />;

  return (
    <>
      {/* Hero */}
      <section className="relative h-[42vh] md:h-[52vh] overflow-hidden">
        <img src={detail.image} alt={sacrament.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2a1a45]/30 to-[#2a1a45]/85" />
        <div className="relative z-10 h-full flex flex-col items-center justify-end text-center pb-14 px-6">
          <p className="text-xs tracking-[0.25em] uppercase text-[#e6d4a8] mb-3">Sacrament</p>
          <h1 className="font-display text-5xl md:text-7xl text-white">{sacrament.title}</h1>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/sacraments" className="inline-flex items-center gap-1 text-sm text-[#3a2359] hover:text-[#5b3a8a] mb-8">
          <ChevronLeft size={16} /> All Sacraments
        </Link>

        <blockquote className="border-l-2 border-[#a68a5b] pl-6 py-2 my-8">
          <p className="font-display text-2xl md:text-3xl text-[#2a1a45] italic leading-snug">{detail.quote}</p>
          <cite className="block mt-3 text-sm text-[#5a534a] not-italic">— {detail.citation}</cite>
        </blockquote>

        <div className="prose max-w-none">
          <EditableText
            contentKey={`sacrament_${slug}_body`}
            label={`${sacrament.title} — Body Text`}
            rows={14}
            defaultValue={detail.body.join('\n\n')}
            paragraphClass="text-[15px] md:text-base text-[#3f3a34] leading-[1.85]"
          />
        </div>

        <div className="mt-12 bg-[#f2ead6]/60 border border-[#e9e0cc] p-8 text-center" data-testid="sacrament-next-steps">
          <h3 className="font-display text-2xl text-[#2a1a45] mb-3">Next Steps</h3>
          <p className="text-sm text-[#5a534a] mb-5">Contact the parish office to begin preparation or ask questions.</p>
          <Link
            to="/contact"
            className="inline-block px-5 py-2.5 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]"
            data-testid="sacrament-contact-btn"
          >
            Contact the Parish Office
          </Link>
        </div>
      </section>
    </>
  );
};

export default SacramentDetail;
